import { TRPC } from '../contexts/TRPC';
import { isLeftResult } from '../lib/trpcTypeGuards';
import { getErrorMessage } from './errorHandling';
import { ensureMediaDecodable } from './mediaValidation';

type TRPCClient = TRPC['client'];

export interface FetchFileOptions {
  fileName?: string;
  mimeType?: string;
}

export async function fetchAndConvertToFile(
  url: string,
  trpc: TRPCClient,
  options?: FetchFileOptions,
): Promise<File> {
  try {
    const result = await trpc.fetchRemoteFile.query({
      url,
      fileName: options?.fileName,
      expectedMimeType: options?.mimeType,
    });

    if (isLeftResult(result)) {
      throw new Error(
        `Failed to fetch remote file for ${url}: ${result.left.message}`,
      );
    }

    const { data, fileName, mimeType } = result.right;

    const byteString = atob(data);
    const byteLength = byteString.length;
    const byteArray = new Uint8Array(byteLength);

    for (let i = 0; i < byteLength; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    const resolvedFileName =
      fileName || options?.fileName || url.split('/').pop() || 'file';
    const resolvedMimeType =
      options?.mimeType || mimeType || 'application/octet-stream';

    const convertedFile = new File([byteArray], resolvedFileName, {
      type: resolvedMimeType,
    });

    try {
      await ensureMediaDecodable(
        convertedFile,
        options?.mimeType || mimeType,
        resolvedFileName,
      );
    } catch (error) {
      throw new Error(
        `Validation failed for fetched file ${resolvedFileName}: ${getErrorMessage(error)}`,
      );
    }

    return convertedFile;
  } catch (error) {
    const message = getErrorMessage(error);
    throw new Error(`Error fetching file from ${url}: ${message}`);
  }
}
